import { useState, type ChangeEvent } from "react";
import { Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSearchPlayers, useSearchUsers } from "../../../hooks/queries/useSuperAdmin";
import { useSetPlayerUserLink } from "../../../hooks/mutations/useSuperAdminMutations";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import { playerToString } from "../../../util/StringUtils";
import type { Player } from "../../../types/domain";

function PlayerSection() {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 300);
  const [editingPlayer, setEditingPlayer] = useState<Player | null>(null);

  const { data: results, isFetching } = useSearchPlayers(
    { q: debouncedQuery, page: 0, size: 25 },
    debouncedQuery.length > 0,
  );

  return (
    <div>
      <Form.Control
        type="text"
        placeholder={t("zakulisi__searchPlayer")}
        value={query}
        onChange={(e: ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
        className="mb-3"
      />
      {isFetching && <Spinner animation="border" size="sm" className="ms-2" />}
      {results && results.content.length > 0 && (
        <Table striped bordered hover size="sm" variant="dark">
          <thead>
            <tr>
              <th style={{ width: "5rem" }}>ID</th>
              <th>{t("player")}</th>
              <th style={{ width: "8rem" }}>{t("zakulisi__userId")}</th>
              <th style={{ width: "10rem" }}></th>
            </tr>
          </thead>
          <tbody>
            {results.content.map((player) => (
              <tr key={player.id}>
                <td>{player.id}</td>
                <td>{playerToString(player)}</td>
                <td>{player.userId ?? "-"}</td>
                <td>
                  <Button variant="outline-info" size="sm" onClick={() => setEditingPlayer(player)}>
                    {t("zakulisi__linkUser")}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {debouncedQuery && results && results.content.length === 0 && !isFetching && (
        <div className="text-center" style={{ color: "var(--text-muted)" }}>{t("auditLog__empty")}</div>
      )}

      <LinkUserModal
        player={editingPlayer}
        onClose={() => setEditingPlayer(null)}
      />
    </div>
  );
}

interface LinkUserModalProps {
  player: Player | null;
  onClose: () => void;
}

function LinkUserModal({ player, onClose }: LinkUserModalProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 300);
  const { data: results, isFetching } = useSearchUsers(
    { q: debouncedQuery, page: 0, size: 15 },
    player != null && debouncedQuery.length > 0,
  );
  const setLinkMutation = useSetPlayerUserLink();

  const setLink = async (userId: number | null) => {
    if (!player) return;
    await setLinkMutation.mutateAsync({ playerId: player.id, userId });
    onClose();
  };

  return (
    <Modal show={player != null} onHide={onClose} size="lg" centered onExited={() => setQuery("")}>
      <Modal.Header closeButton style={{ backgroundColor: "var(--bg-secondary)", color: "var(--text-primary)", borderColor: "var(--border-color)" }}>
        <Modal.Title>
          {t("zakulisi__linkUserFor")}: <strong>{player && playerToString(player)}</strong>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ backgroundColor: "var(--bg-secondary)", color: "var(--text-primary)" }}>
        {player?.userId != null && (
          <div className="mb-3" style={{ color: "var(--text-muted)", fontSize: "0.9em" }}>
            Currently linked to userId={player.userId}
          </div>
        )}
        <div className="d-flex gap-2 mb-3">
          <Form.Control
            type="text"
            placeholder={t("zakulisi__searchUser")}
            value={query}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
            autoFocus
          />
          {player?.userId != null && (
            <Button variant="outline-danger" onClick={() => setLink(null)} disabled={setLinkMutation.isPending}>
              {t("zakulisi__unlink")}
            </Button>
          )}
        </div>

        {isFetching && <Spinner animation="border" size="sm" />}
        {results && results.content.length > 0 && (
          <Table striped bordered hover size="sm" variant="dark">
            <thead>
              <tr>
                <th style={{ width: "5rem" }}>ID</th>
                <th>{t("username")}</th>
                <th style={{ width: "6rem" }}>{t("auditLog__action")}</th>
              </tr>
            </thead>
            <tbody>
              {results.content.map((user) => (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td>{user.username}</td>
                  <td>
                    <Button
                      variant="outline-success"
                      size="sm"
                      onClick={() => setLink(user.id)}
                      disabled={setLinkMutation.isPending || user.id === player?.userId}
                    >
                      {user.id === player?.userId ? "✓" : t("zakulisi__link")}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal.Body>
      <Modal.Footer style={{ backgroundColor: "var(--bg-secondary)", borderColor: "var(--border-color)" }}>
        <Button variant="outline-secondary" onClick={onClose}>{t("cancel")}</Button>
      </Modal.Footer>
    </Modal>
  );
}

export default PlayerSection;
